import { forwardRef } from 'react';

import { styled } from '@spazious/dollhouse-theme';
import { useForkRef } from '@spazious/react-hooks';

import { StickyContainer, StickyContainerProps } from '../Sticky/index.js';

import type { DataTableHookResult } from './hooks/useDataTable.js';
import { DataTableProvider, DataTableProviderProps } from './DataTableProvider.js';
import { DataTableRow } from './DataTableTypes.js';

export type DataTableElement = HTMLDivElement;

export type DataTableProps<TRow extends DataTableRow, TContext = unknown> = DataTableHookResult<
  TRow,
  TContext
> &
  Pick<DataTableProviderProps<TRow, TContext>, 'context'> &
  Omit<StickyContainerProps, 'id'> & {
    stickyId?: string;
    children?: React.ReactNode;
  };

export const DataTable = styled(
  forwardRef(
    <TRow extends DataTableRow, TContext = unknown>(
      {
        children,
        columns,
        rows,
        register,
        unregister,
        update,
        containerRef,
        manager,
        context,
        stickyId = 'dataTable',
        ...restProps
      }: DataTableProps<TRow, TContext>,
      ref: React.ForwardedRef<DataTableElement>
    ) => {
      const forkedRef = useForkRef(containerRef, ref) as React.RefObject<DataTableElement>;

      return (
        <DataTableProvider
          columns={columns}
          context={context}
          manager={manager}
          register={register}
          rows={rows}
          unregister={unregister}
          update={update}
        >
          <StickyContainer ref={forkedRef} id={stickyId} {...restProps}>
            {children}
          </StickyContainer>
        </DataTableProvider>
      );
    }
  )
).withConfig({
  displayName: 'DataTable',
})`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
